import React from 'react';
import classNames from 'classnames';
import {
    NSRadioOption, NSButton, NSCheckbox, NSDropdown, NSInput,
} from 'bricks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faPercentage } from '@fortawesome/pro-regular-svg-icons';
import { ISubproject } from 'views/ProFormaTable/types';
import { IOptionAnyValue } from 'bricks/types';

interface ProjectItemProps {
    project: ISubproject;
    isLast: boolean;
    selectedProjectId?: number;
    onProjectSelect: (projectId: number) => void;
    checkboxStates: Record<string, boolean>;
    handleCheckboxChange: (id: string) => void;
    selectedScenarios: Record<number, IOptionAnyValue | undefined>;
    handleScenarioSelect: (projectId: number) => (option: IOptionAnyValue | undefined) => void;
    budgetEscalation: string;
    handleBudgetEscalationChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ProjectItem: React.FC<ProjectItemProps> = ({
    project,
    isLast,
    selectedProjectId,
    onProjectSelect,
    checkboxStates,
    handleCheckboxChange,
    selectedScenarios,
    handleScenarioSelect,
    budgetEscalation,
    handleBudgetEscalationChange,
}) => {
    const budgetCatId = `budget-cat-${project.id}`;
    const budgetEstId = `budget-est-${project.id}`;
    const cashFlowId = `cash-flow-${project.id}`;
    const selectedScenario = selectedScenarios[project.id];
    const isSelected = project.id === selectedProjectId;

    return (
        <div>
            <div
                className={classNames('ImportSourceListItem cursor--pointer p-2', {
                    'ImportSourceListItem--selected': isSelected,
                })}
                onClick={() => onProjectSelect(project.id)}
                role="presentation"
            >
                <div className="d-flex align-items-start">
                    <div className="mr-2">
                        <NSRadioOption
                            id={`radio-${project.id}`}
                            name="projectSelection"
                            checked={isSelected}
                            onChange={() => onProjectSelect(project.id)}
                            label=""
                        />
                    </div>
                    <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <div className="font-12 text-muted">{project.developmentProject.projectName}</div>
                                <div className="text-white font-weight-bold">{project.subprojectName}</div>
                            </div>
                            {isSelected && (
                                <NSButton
                                    text="Import"
                                    color="primary"
                                    icon={faAngleRight}
                                    iconPosition="right"
                                    className="px-3"
                                    disabled={project.earlyPlanning && !selectedScenario}
                                />
                            )}
                        </div>
                        {isSelected && (
                            <div className="mt-2">
                                <div className="separator mb-2" />
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-white">
                                        Import from
                                        {' '}
                                        {project.subprojectName}
                                        :
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <div className="d-flex flex-column gap-2">
                                        <NSCheckbox
                                            id={budgetCatId}
                                            labelText="Budget categorization structure and names"
                                            checked={checkboxStates[budgetCatId] ?? true}
                                            callback={() => handleCheckboxChange(budgetCatId)}
                                            size="sm"
                                            iconClassName="mr-2"
                                            labelClassName="text-white font-weight-normal font-12 d-flex align-items-center"
                                        />
                                        <NSCheckbox
                                            id={budgetEstId}
                                            labelText="Budget estimates ($/SF)"
                                            checked={checkboxStates[budgetEstId] ?? true}
                                            callback={() => handleCheckboxChange(budgetEstId)}
                                            size="sm"
                                            iconClassName="mr-2"
                                            labelClassName="text-white font-weight-normal font-12 d-flex align-items-center"
                                        />
                                        <NSCheckbox
                                            id={cashFlowId}
                                            labelText="Cash flow curve fits"
                                            checked={checkboxStates[cashFlowId] ?? true}
                                            callback={() => handleCheckboxChange(cashFlowId)}
                                            size="sm"
                                            iconClassName="mr-2"
                                            labelClassName="text-white font-weight-normal font-12 d-flex align-items-center"
                                        />
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center ml-4">
                                        {project.earlyPlanning && (
                                            <div className="mr-3" style={{ width: '320px' }}>
                                                <div className="text-white mb-1">
                                                    Scenario
                                                    <span className="text-danger pl-1">*</span>
                                                </div>
                                                <div>
                                                    <NSDropdown
                                                        options={project.budgetScenarios?.map(scenario => ({
                                                            value: scenario.correlationId,
                                                            label: scenario.name,
                                                        })) || []}
                                                        placeholder="Select scenario"
                                                        handleSelectOption={handleScenarioSelect(project.id)}
                                                        selectedOption={selectedScenario}
                                                        isFullWidth
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-white mb-1">Budget escalation factor</div>
                                            <div className="">
                                                <NSInput
                                                    type="number"
                                                    name="budgetEscalation"
                                                    placeholder="Enter amount"
                                                    value={budgetEscalation}
                                                    onChange={handleBudgetEscalationChange}
                                                    appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                                                    max={100}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!isSelected && !isLast && <div className="border-bottom my-0 mx-3 opacity-10" />}
        </div>
    );
};

export default ProjectItem;
